import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, 
  Search, 
  Mail, 
  Edit, 
  Trash2, 
  ChevronLeft, 
  ChevronRight,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Users
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

const ITEMS_PER_PAGE = 10;

const GmailAccountsManager = () => {
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    status: "pendente"
  });
  
  const [editingAccount, setEditingAccount] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(null);
  const [showPassword, setShowPassword] = useState({});
  const [showBlockWarning, setShowBlockWarning] = useState(false);

  // Fetch accounts
  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/gmail-accounts`, {
        headers: {
          "X-API-Key": API_KEY,
        },
      });

      if (!response.ok) {
        throw new Error("Falha ao carregar contas");
      }

      const data = await response.json();
      setAccounts(data);
      setFilteredAccounts(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Search filter
  useEffect(() => {
    const filtered = accounts.filter((account) =>
      account.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredAccounts(filtered);
    setCurrentPage(1);
  }, [searchTerm, accounts]);

  // Pagination
  const totalPages = Math.ceil(filteredAccounts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentAccounts = filteredAccounts.slice(startIndex, endIndex);

  // Handlers
  const handleAddAccount = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/gmail-accounts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": API_KEY,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Falha ao criar conta");
      }

      setShowAddModal(false);
      setFormData({ email: "", password: "", status: "pendente" });
      fetchAccounts();
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditAccount = async () => {
    try {
      // Se estiver mudando para bloqueada, mostrar confirmação
      if (formData.status === "bloqueada" && editingAccount.status !== "bloqueada") {
        setShowBlockWarning(true);
        return;
      }

      const response = await fetch(
        `${BACKEND_URL}/api/gmail-accounts/${editingAccount.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": API_KEY,
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Falha ao atualizar conta");
      }

      setShowEditModal(false);
      setEditingAccount(null);
      setFormData({ email: "", password: "", status: "ativa" });
      fetchAccounts();
    } catch (error) {
      console.error(error);
    }
  };

  const confirmBlockAccount = async () => {
    try {
      // Deletar cookies relacionados
      await fetch(`${BACKEND_URL}/api/cookies/${formData.email}`, {
        method: "DELETE",
        headers: {
          "X-API-Key": API_KEY,
        },
      });

      // Atualizar a conta
      const response = await fetch(
        `${BACKEND_URL}/api/gmail-accounts/${editingAccount.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": API_KEY,
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Falha ao atualizar conta");
      }

      setShowBlockWarning(false);
      setShowEditModal(false);
      setEditingAccount(null);
      setFormData({ email: "", password: "", status: "pendente" });
      fetchAccounts();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/gmail-accounts/${deletingAccount.id}`,
        {
          method: "DELETE",
          headers: {
            "X-API-Key": API_KEY,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Falha ao deletar conta");
      }

      setShowDeleteDialog(false);
      setDeletingAccount(null);
      fetchAccounts();
    } catch (error) {
      console.error(error);
    }
  };

  const openEditModal = (account) => {
    setEditingAccount(account);
    setFormData({
      email: account.email,
      password: account.password,
      status: account.status,
    });
    setShowEditModal(true);
  };

  const openDeleteDialog = (account) => {
    setDeletingAccount(account);
    setShowDeleteDialog(true);
  };

  const togglePasswordVisibility = (accountId) => {
    setShowPassword(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <Button
          onClick={() => {
            setFormData({ email: "", password: "", status: "pendente" });
            setShowAddModal(true);
          }}
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white ml-auto"
        >
          <Plus className="h-4 w-4 mr-2" />
          Adicionar Conta
        </Button>
      </div>

      {/* Search Bar */}
      <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Buscar por email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-[#0a0a0a] border-gray-700 text-white placeholder:text-gray-500"
          />
        </div>
      </div>

      {/* Stats Cards - Grid Responsivo */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
        {/* Total de Contas */}
        <div className="group relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-gray-800 rounded-lg p-4 lg:p-5 hover:border-gray-700 transition-all duration-300 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
          
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-3">
              <div className="h-10 w-10 lg:h-11 lg:w-11 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Users className="h-5 w-5 lg:h-5.5 lg:w-5.5 text-white" />
              </div>
            </div>
            
            <div className="space-y-1">
              <h3 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
                {accounts.length}
              </h3>
              <p className="text-xs lg:text-sm text-gray-400 font-medium">
                Total de Contas
              </p>
            </div>
          </div>
          
          <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-blue-500 to-blue-600 blur-xl -z-10"></div>
        </div>

        {/* Contas Ativas */}
        <div className="group relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-gray-800 rounded-lg p-4 lg:p-5 hover:border-gray-700 transition-all duration-300 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-green-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
          
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-3">
              <div className="h-10 w-10 lg:h-11 lg:w-11 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <CheckCircle2 className="h-5 w-5 lg:h-5.5 lg:w-5.5 text-white" />
              </div>
            </div>
            
            <div className="space-y-1">
              <h3 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
                {accounts.filter((a) => a.status === "ativa").length}
              </h3>
              <p className="text-xs lg:text-sm text-gray-400 font-medium">
                Contas Ativas
              </p>
            </div>
          </div>
          
          <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-green-500 to-green-600 blur-xl -z-10"></div>
        </div>

        {/* Contas Bloqueadas */}
        <div className="group relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-gray-800 rounded-lg p-4 lg:p-5 hover:border-gray-700 transition-all duration-300 overflow-hidden col-span-2 lg:col-span-1">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-red-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
          
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-3">
              <div className="h-10 w-10 lg:h-11 lg:w-11 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <XCircle className="h-5 w-5 lg:h-5.5 lg:w-5.5 text-white" />
              </div>
            </div>
            
            <div className="space-y-1">
              <h3 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
                {accounts.filter((a) => a.status === "bloqueada").length}
              </h3>
              <p className="text-xs lg:text-sm text-gray-400 font-medium">
                Contas Bloqueadas
              </p>
            </div>
          </div>
          
          <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-red-500 to-red-600 blur-xl -z-10"></div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-800 hover:bg-gray-900">
                <TableHead className="text-gray-400">Email</TableHead>
                <TableHead className="text-gray-400">Senha</TableHead>
                <TableHead className="text-gray-400">Status</TableHead>
                <TableHead className="text-gray-400 whitespace-nowrap">Data de Criação</TableHead>
                <TableHead className="text-gray-400 text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-400">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : currentAccounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-400">
                    {searchTerm
                      ? "Nenhuma conta encontrada"
                      : "Nenhuma conta cadastrada"}
                  </TableCell>
                </TableRow>
              ) : (
                currentAccounts.map((account) => (
                  <TableRow
                    key={account.id}
                    className="border-gray-800 hover:bg-gray-900"
                  >
                    <TableCell className="text-white font-medium">
                      {account.email}
                    </TableCell>
                    <TableCell className="text-white">
                      <div className="flex items-center gap-2">
                        <span className="font-mono">
                          {showPassword[account.id] ? account.password : "••••••••"}
                        </span>
                        <button
                          onClick={() => togglePasswordVisibility(account.id)}
                          className="text-gray-400 hover:text-white transition-colors"
                        >
                          {showPassword[account.id] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          account.status === "ativa" 
                            ? "default" 
                            : account.status === "pendente" 
                            ? "secondary" 
                            : "destructive"
                        }
                        className={
                          account.status === "ativa"
                            ? "bg-green-500/10 text-green-500 border-green-500/50"
                            : account.status === "pendente"
                            ? "bg-yellow-500/10 text-yellow-500 border-yellow-500/50"
                            : "bg-red-500/10 text-red-500 border-red-500/50"
                        }
                      >
                        {account.status === "ativa" ? (
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                        ) : account.status === "pendente" ? (
                          <AlertTriangle className="h-3 w-3 mr-1" />
                        ) : (
                          <XCircle className="h-3 w-3 mr-1" />
                        )}
                        {account.status === "ativa" 
                          ? "Ativa" 
                          : account.status === "pendente" 
                          ? "Pendente" 
                          : "Bloqueada"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-400">
                      {new Date(account.created_at).toLocaleDateString("pt-BR")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(account)}
                          className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(account)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-4 border-t border-gray-800">
            <p className="text-xs sm:text-sm text-gray-400">
              Página {currentPage} de {totalPages} • {filteredAccounts.length} conta(s)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="bg-gray-800 border-gray-700 text-white disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="bg-gray-800 border-gray-700 text-white disabled:opacity-50"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add Account Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#1a1a1a] border-gray-800 text-white max-w-md w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>Adicionar Nova Conta Gmail</DialogTitle>
            <DialogDescription className="text-gray-400">
              Preencha os dados da conta de email do Gmail
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="exemplo@gmail.com"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="bg-[#0a0a0a] border-gray-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="text"
                placeholder="Digite a senha"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                className="bg-[#0a0a0a] border-gray-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData({ ...formData, status: value })
                }
              >
                <SelectTrigger className="bg-[#0a0a0a] border-gray-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-gray-800 text-white">
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="ativa">Ativa</SelectItem>
                  <SelectItem value="bloqueada">Bloqueada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
              className="bg-gray-800 border-gray-700 text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddAccount}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
            >
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Account Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#1a1a1a] border-gray-800 text-white max-w-md w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>Editar Conta Gmail</DialogTitle>
            <DialogDescription className="text-gray-400">
              Atualize os dados da conta de email
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                placeholder="exemplo@gmail.com"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="bg-[#0a0a0a] border-gray-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-password">Senha</Label>
              <Input
                id="edit-password"
                type="text"
                placeholder="Digite a senha"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                className="bg-[#0a0a0a] border-gray-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData({ ...formData, status: value })
                }
              >
                <SelectTrigger className="bg-[#0a0a0a] border-gray-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-gray-800 text-white">
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="ativa">Ativa</SelectItem>
                  <SelectItem value="bloqueada">Bloqueada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowEditModal(false)}
              className="bg-gray-800 border-gray-700 text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleEditAccount}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
            >
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[#1a1a1a] border-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Confirmar exclusão
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Tem certeza que deseja deletar a conta{" "}
              <span className="font-semibold text-white">
                {deletingAccount?.email}
              </span>
              ? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-white border-gray-700 hover:bg-gray-700">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Block Account Warning Dialog */}
      <AlertDialog open={showBlockWarning} onOpenChange={setShowBlockWarning}>
        <AlertDialogContent className="bg-[#1a1a1a] border-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Atenção: Bloquear Conta
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Ao bloquear a conta{" "}
              <span className="font-semibold text-white">
                {formData.email}
              </span>
              , todos os cookies relacionados a este email serão excluídos permanentemente.
              <br /><br />
              <span className="text-yellow-500 font-medium">
                Esta ação não pode ser desfeita.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setShowBlockWarning(false);
                setFormData({ ...formData, status: editingAccount.status });
              }}
              className="bg-gray-800 text-white border-gray-700 hover:bg-gray-700"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBlockAccount}
              className="bg-yellow-600 text-white hover:bg-yellow-700"
            >
              Confirmar Bloqueio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GmailAccountsManager;
